import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";

import { Card, CardContent, Skeleton } from "@kuzco/web/ui";

import type { LeaderboardRange } from "../../../server/routers/leaderboard.helpers";
import { MemberApiKeyCard } from "../../../components/admin/members/MemberApiKeyCard";
import { MemberCumulativeChart } from "../../../components/admin/members/MemberCumulativeChart";
import { MemberHeader } from "../../../components/admin/members/MemberHeader";
import { MemberHeatmap } from "../../../components/admin/members/MemberHeatmap";
import { MemberStatsGrid } from "../../../components/admin/members/MemberStatsGrid";
import { MemberTimeseriesChart } from "../../../components/admin/members/MemberTimeseriesChart";
import { LeaderboardRangePicker } from "../../../components/leaderboard/LeaderboardRangePicker";
import { ALL_TIME_START_MS } from "../../../server/routers/admin-usage.helpers";
import { nubankTrpc } from "../../../trpc";

export const Route = createFileRoute("/_authed/admin/members/$userId")({
  component: MemberDetailPage,
  head: () => ({
    meta: [{ title: "Member | NBDC" }],
  }),
});

const DAY_MS = 24 * 60 * 60 * 1000;

function rangeWindow(range: LeaderboardRange, now: number) {
  if (range === "today") {
    return {
      startMs: now - DAY_MS,
      endMs: now,
      granularity: "1h" as const,
    };
  }
  if (range === "week") {
    return {
      startMs: now - 7 * DAY_MS,
      endMs: now,
      granularity: "1h" as const,
    };
  }
  if (range === "month") {
    return {
      startMs: now - 30 * DAY_MS,
      endMs: now,
      granularity: "1d" as const,
    };
  }
  return {
    startMs: ALL_TIME_START_MS,
    endMs: now,
    granularity: "1d" as const,
  };
}

function MemberDetailPage() {
  const { userId } = Route.useParams();
  const [range, setRange] = useState<LeaderboardRange>("today");

  // Pin the window once per range change so refetches don't drift the bounds.
  const window = useMemo(() => rangeWindow(range, Date.now()), [range]);
  // Heatmap is always trailing 365d regardless of the chart range above.
  const heatmapWindow = useMemo(() => {
    const now = Date.now();
    return { startMs: now - 365 * DAY_MS, endMs: now };
  }, []);

  const detailsQuery = nubankTrpc.admin.members.getDetails.useQuery({
    userId,
  });
  const timeseriesQuery = nubankTrpc.admin.members.getTimeseries.useQuery({
    userId,
    startMs: window.startMs,
    endMs: window.endMs,
    granularity: window.granularity,
  });
  const heatmapQuery = nubankTrpc.admin.members.getHeatmap.useQuery({
    userId,
    startMs: heatmapWindow.startMs,
    endMs: heatmapWindow.endMs,
  });
  const cumulativeQuery = nubankTrpc.admin.members.getCumulative.useQuery({
    userId,
  });

  if (detailsQuery.isPending) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (detailsQuery.isError) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-sm text-detail-failure">
            {detailsQuery.error.message}
          </p>
        </CardContent>
      </Card>
    );
  }

  const { member, summary, apiKey } = detailsQuery.data;

  return (
    <div className="flex flex-col gap-6">
      <MemberHeader
        name={member.name}
        email={member.email}
        membershipRole={member.membershipRole}
      />

      <MemberStatsGrid
        joinedAt={member.joinedAt}
        lastUsedAt={summary.lastUsedAt}
        totalRequests={summary.requestCount}
        totalTokens={summary.totalTokens}
        totalCost={summary.totalCost}
      />

      <MemberApiKeyCard apiKey={apiKey} />

      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Usage</h2>
        <LeaderboardRangePicker value={range} onChange={setRange} />
      </div>

      {timeseriesQuery.isPending ? (
        <Skeleton className="h-[600px] w-full" />
      ) : timeseriesQuery.isError ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-detail-failure">
              {timeseriesQuery.error.message}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          <MemberTimeseriesChart
            title="Requests"
            data={timeseriesQuery.data.buckets}
            granularity={window.granularity}
            variant="requests"
          />
          <MemberTimeseriesChart
            title="Tokens"
            data={timeseriesQuery.data.buckets}
            granularity={window.granularity}
            variant="tokens"
          />
          <MemberTimeseriesChart
            title="Cost"
            data={timeseriesQuery.data.buckets}
            granularity={window.granularity}
            variant="cost"
          />
        </div>
      )}

      <h2 className="text-lg font-semibold">Activity</h2>
      {heatmapQuery.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : heatmapQuery.isError ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-detail-failure">
              {heatmapQuery.error.message}
            </p>
          </CardContent>
        </Card>
      ) : (
        <MemberHeatmap days={heatmapQuery.data.days} />
      )}

      <h2 className="text-lg font-semibold">All time</h2>
      {cumulativeQuery.isPending ? (
        <Skeleton className="h-[600px] w-full" />
      ) : cumulativeQuery.isError ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-detail-failure">
              {cumulativeQuery.error.message}
            </p>
          </CardContent>
        </Card>
      ) : cumulativeQuery.data.points.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">
              No usage history yet.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          <MemberCumulativeChart
            title="Cumulative requests"
            data={cumulativeQuery.data.points}
            variant="requests"
            total={cumulativeQuery.data.totals.requests}
          />
          <MemberCumulativeChart
            title="Cumulative tokens"
            data={cumulativeQuery.data.points}
            variant="tokens"
            total={cumulativeQuery.data.totals.tokens}
          />
          <MemberCumulativeChart
            title="Cumulative cost"
            data={cumulativeQuery.data.points}
            variant="cost"
            total={cumulativeQuery.data.totals.cost}
          />
        </div>
      )}
    </div>
  );
}
